/**
 * Print UI - Premium Configurator
 * Custom JavaScript for IBLAprint print product configurator
 */

(function () {
    'use strict';

    // Configuration
    const CONFIG = {
        currentStep: 1,
        totalSteps: 3,
        selections: {
            format: '',
            quantity: '',
            paper: '',
            finishing: '',
            firstName: '',
            lastName: '',
            email: ''
        }
    };

    // DOM Elements
    let elements = {};

    // Initialize
    function init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', setup);
        } else {
            setup();
        }
    }

    // Setup
    function setup() {
        cacheElements();
        attachEventListeners();
        updateUI();

        // Mobile drawer setup
        if (window.innerWidth < 990) {
            setupMobileDrawer();
        }
    }

    // Cache DOM elements
    function cacheElements() {
        elements = {
            configurator: document.querySelector('.print-configurator'),
            progressFill: document.querySelector('.progress-fill'),
            progressSteps: document.querySelectorAll('.progress-step'),
            progressDots: document.querySelectorAll('.progress-dot'),
            stepWrappers: document.querySelectorAll('.step-wrapper'),
            summaryPill: document.querySelector('.summary-pill'),
            nextButtons: document.querySelectorAll('[data-step-next]'),
            prevButtons: document.querySelectorAll('[data-step-prev]'),
            submitButton: document.querySelector('[data-step-submit]'),
            form: document.querySelector('.configurator-form'),

            // Option inputs
            formatInputs: document.querySelectorAll('[name="properties[Format]"]'),
            quantityInputs: document.querySelectorAll('[name="quantity"]'),
            paperInputs: document.querySelectorAll('[name="properties[Paper]"]'),
            finishingInputs: document.querySelectorAll('[name="properties[Finishing]"]'),

            // Contact fields
            firstNameInput: document.querySelector('[name="properties[First Name]"]'),
            lastNameInput: document.querySelector('[name="properties[Last Name]"]'),
            emailInput: document.querySelector('[name="properties[Email]"]'),
            privacyCheckbox: document.querySelector('[name="properties[Privacy]"]')
        };
    }

    // Attach event listeners
    function attachEventListeners() {
        // Next buttons
        elements.nextButtons.forEach(btn => {
            btn.addEventListener('click', handleNext);
        });

        // Previous buttons
        elements.prevButtons.forEach(btn => {
            btn.addEventListener('click', handlePrevious);
        });

        // Submit button
        if (elements.submitButton) {
            elements.submitButton.addEventListener('click', handleSubmit);
        }

        // Option change listeners
        addChangeListeners(elements.formatInputs, 'format');
        addChangeListeners(elements.quantityInputs, 'quantity');
        addChangeListeners(elements.paperInputs, 'paper');
        addChangeListeners(elements.finishingInputs, 'finishing');

        // Contact field listeners
        if (elements.firstNameInput) {
            elements.firstNameInput.addEventListener('input', (e) => {
                CONFIG.selections.firstName = e.target.value;
            });
        }
        if (elements.lastNameInput) {
            elements.lastNameInput.addEventListener('input', (e) => {
                CONFIG.selections.lastName = e.target.value;
            });
        }
        if (elements.emailInput) {
            elements.emailInput.addEventListener('input', (e) => {
                CONFIG.selections.email = e.target.value;
            });
        }
    }

    // Add change listeners to option groups
    function addChangeListeners(inputs, key) {
        inputs.forEach(input => {
            input.addEventListener('change', (e) => {
                CONFIG.selections[key] = e.target.value;
                updateSummaryPill();
            });
        });
    }

    // Handle next button
    function handleNext(e) {
        e.preventDefault();

        const currentStepNum = CONFIG.currentStep;

        // Validate current step
        if (!validateStep(currentStepNum)) {
            return;
        }

        // Mark current step as completed
        markStepCompleted(currentStepNum);

        // Move to next step
        if (CONFIG.currentStep < CONFIG.totalSteps) {
            CONFIG.currentStep++;
            goToStep(CONFIG.currentStep);
        }
    }

    // Handle previous button
    function handlePrevious(e) {
        e.preventDefault();

        if (CONFIG.currentStep > 1) {
            CONFIG.currentStep--;
            goToStep(CONFIG.currentStep, true);
        }
    }

    // Handle submit
    function handleSubmit(e) {
        if (!validateStep(3)) {
            e.preventDefault();
            return false;
        }

        // Form will submit naturally
        return true;
    }

    // Validate step
    function validateStep(stepNum) {
        let isValid = true;

        if (stepNum === 1) {
            // Validate format and quantity
            if (!CONFIG.selections.format) {
                showError('Please select a format');
                isValid = false;
            }
            if (!CONFIG.selections.quantity) {
                showError('Please select a quantity');
                isValid = false;
            }
        } else if (stepNum === 2) {
            // Validate paper type
            if (!CONFIG.selections.paper) {
                showError('Please select a paper type');
                isValid = false;
            }
        } else if (stepNum === 3) {
            // Validate contact info
            if (!CONFIG.selections.firstName || !CONFIG.selections.lastName) {
                showError('Please enter your name');
                isValid = false;
            }
            if (!CONFIG.selections.email || !isValidEmail(CONFIG.selections.email)) {
                showError('Please enter a valid email');
                isValid = false;
            }
            if (elements.privacyCheckbox && !elements.privacyCheckbox.checked) {
                showError('Please accept the privacy policy');
                isValid = false;
            }
        }

        return isValid;
    }

    // Email validation
    function isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    // Show error
    function showError(message) {
        // Simple alert for now - can be enhanced with custom UI
        alert(message);
    }

    // Mark step as completed
    function markStepCompleted(stepNum) {
        const stepIndex = stepNum - 1;
        if (elements.progressSteps[stepIndex]) {
            elements.progressSteps[stepIndex].classList.add('completed');

            // Add checkmark animation
            const dot = elements.progressDots[stepIndex];
            if (dot && !dot.querySelector('.checkmark-icon')) {
                dot.innerHTML = '<svg class="checkmark-icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8L6 11L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            }
        }
    }

    // Go to step
    function goToStep(stepNum, isBack = false) {
        // Hide all steps
        elements.stepWrappers.forEach((wrapper, index) => {
            wrapper.classList.remove('active', 'prev');
            if (index + 1 < stepNum) {
                wrapper.classList.add('prev');
            }
        });

        // Show target step
        const targetStep = elements.stepWrappers[stepNum - 1];
        if (targetStep) {
            requestAnimationFrame(() => {
                targetStep.classList.add('active');
            });
        }

        // Update progress
        updateProgress();

        // Update active step indicator
        elements.progressSteps.forEach((step, index) => {
            step.classList.remove('active');
            if (index + 1 === stepNum) {
                step.classList.add('active');
            }
        });

        // Scroll to top of configurator
        if (elements.configurator) {
            elements.configurator.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    // Update progress bar
    function updateProgress() {
        if (!elements.progressFill) return;

        const progress = ((CONFIG.currentStep - 1) / (CONFIG.totalSteps - 1)) * 100;
        elements.progressFill.style.width = `${progress}%`;
    }

    // Update summary pill
    function updateSummaryPill() {
        if (!elements.summaryPill) return;

        const parts = [];

        if (CONFIG.selections.format) {
            parts.push(`<span class="summary-pill-item">${CONFIG.selections.format}</span>`);
        }
        if (CONFIG.selections.paper) {
            parts.push(`<span class="summary-pill-item">${CONFIG.selections.paper}</span>`);
        }
        if (CONFIG.selections.finishing && CONFIG.selections.finishing !== 'None') {
            parts.push(`<span class="summary-pill-item">${CONFIG.selections.finishing}</span>`);
        }
        if (CONFIG.selections.quantity) {
            parts.push(`<span class="summary-pill-item">${CONFIG.selections.quantity} pcs</span>`);
        }

        if (parts.length > 0) {
            elements.summaryPill.innerHTML = parts.join('');
            elements.summaryPill.style.display = 'inline-flex';
        } else {
            elements.summaryPill.style.display = 'none';
        }
    }

    // Update UI
    function updateUI() {
        updateProgress();
        updateSummaryPill();
    }

    // Setup mobile drawer
    function setupMobileDrawer() {
        if (!elements.configurator) return;

        const handle = document.createElement('div');
        handle.className = 'configurator-handle';
        elements.configurator.insertBefore(handle, elements.configurator.firstChild);

        handle.addEventListener('click', () => {
            elements.configurator.classList.toggle('open');
        });

        // Close drawer when clicking outside
        document.addEventListener('click', (e) => {
            if (!elements.configurator.contains(e.target) && elements.configurator.classList.contains('open')) {
                elements.configurator.classList.remove('open');
            }
        });
    }

    // Gallery interactions
    function setupGallery() {
        const mainImage = document.querySelector('.gallery-main img');
        const thumbnails = document.querySelectorAll('.filmstrip-thumb');

        thumbnails.forEach(thumb => {
            thumb.addEventListener('click', () => {
                const newSrc = thumb.querySelector('img').src;
                if (mainImage) {
                    mainImage.src = newSrc;
                }

                // Update active state
                thumbnails.forEach(t => t.classList.remove('active'));
                thumb.classList.add('active');
            });
        });

        // Tile click to update main image
        const tiles = document.querySelectorAll('.gallery-tile');
        tiles.forEach(tile => {
            tile.addEventListener('click', () => {
                const newSrc = tile.querySelector('img').src;
                if (mainImage) {
                    mainImage.src = newSrc;
                }
            });
        });
    }

    // Initialize
    init();

    // Setup gallery when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupGallery);
    } else {
        setupGallery();
    }

})();
